import React, { useCallback, useEffect, useMemo, useState } from "react";
import RichTextEditor from "./rich-text-editor";
import "./editor.css";
import { ConvertStringToDate, getUserDetails, isValidDate, makeGetCall, makePostCall, RADIOLOGY_URL, removeContentById } from "../../utils/helper";
import { useReportStatus } from "../../hooks/useReportStatus";
import moment from "moment";
import { Button, Card, Checkbox, message, Modal, Radio, Select, Tag, Dropdown, Menu, Popover } from "antd";
import { TemplateHeader } from "./constants";
import { RightSquareOutlined, DeleteOutlined, ExclamationCircleOutlined, UserOutlined, DownOutlined, UpOutlined, BarChartOutlined, FolderOutlined, SettingOutlined, MoreOutlined, FileTextOutlined, BellOutlined } from '@ant-design/icons';
import { Link } from "react-router-dom";
import CriticalFinding from "./critical-finding";
import { launchViewerOnDualMonitor, checkMonitorSetup } from '../../utils/MonitorManager';
import PdfViewer from "../../components/PdfViewer";
import VoiceTranscribeButton from "../../components/VoiceTranscribeButton";
import { SHOW_ALL_ADVANCED_OPTIONS } from "../../constants/APP_CONSTANTS";

const ReportEditor = ({ cancel, onSave, patientDetails, selected_report, handlePostSaving }) => {
  const [content, setContent] = React.useState(null);
  const editorRef = React.useRef(null);
  const [reportsData, setReportsData] = React.useState([]);
  const [priorReports, setPriorReports] = React.useState([]);
  const [currentReport, setCurrentReport] = React.useState(null);
  const [templates, setTemplates] = React.useState([]);
  const [nodes, setNodes] = React.useState([]);
  const [selectedNode, setSelectedNode] = React.useState(null);
  const [proxyUser, setProxyUser] = React.useState(null);
  const [cosigningDoctor, setCosigningDoctor] = React.useState(null);
  const [moreAction, setMoreAction] = React.useState(null);
  const [radUsers, setRadUsers] = React.useState([]);
  const [allUsers, setAllUsers] = React.useState([]);
  const userType = getUserDetails().user_type;
  const [correlated, setCorrelated] = useState(null);
  const [diagnosed, setDiagnosed] = useState(null);
  const [submitTriggered, setSubmitTrigged] = useState(false);
  const [correlatedMandatory, setCorrelatedMandatory] = useState(false);
  const [criticalFindingModal, setCriticalFindingModal] = useState({ visible: false, data: {} });
  const [viewerModal, setViewerModal] = useState({ visible: false, data: {} });
  const [loadModalVisible, setLoadModalVisible] = useState(false);
  const [pendingHtml, setPendingHtml] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editorInitialized, setEditorInitialized] = useState(false);
  const [showAdvanceOptions, setShowAdvanceOptions] = useState(false);

  const [expandedSections, setExpandedSections] = useState({
    reportsHistory: true,
    priorReports: false,
    documents: false,
    advancedOptions: false
  });
  const [isSignedOff, setIsSignedOff] = useState(false);

  // Use database-driven status system
  const { statusList, loading: statusLoading, error: statusError } = useReportStatus();

  const { pacs_order } = patientDetails;
  const { patient } = pacs_order;

  // Status mapping - frontend to backend
  const statusMap = {
    'DRAFTED': 'draft',
    'SIGNEDOFF': 'signoff'
  };

  // Reverse mapping - backend to frontend
  const reverseStatusMap = {
    'draft': 'DRAFTED',
    'signoff': 'SIGNEDOFF'
  };

  const handleContentChange = (newContent) => {
    setContent(newContent);
    // Mark editor as initialized after first content change
    if (!editorInitialized) {
      setEditorInitialized(true);
    }
  }

  useEffect(() => {
    // getTemplates();
    getNodes();
    fetchRadUsers();
    fetchAllUsers();
  }, []);

  useEffect(() => {
    fetchPrevReports();
    fetchPriorReports();

    setSelectedNode(null);
    setSelectedTemplate(null);
    setTemplates([]);
  }, [patientDetails]);

  const fetchPriorReports = () => {
    makePostCall('/get-prior-reports', {
      yh_no: patient?.pat_pin,
      his_ord_no: pacs_order?.po_his_ord_no,
      acc_no: pacs_order?.po_acc_no,
      order_id: pacs_order.pacs_ord_id,
    }).then(res => {
      setPriorReports(res.data?.data || []);
    }).catch(e => {
      console.log(e);
      setPriorReports([]);
    })
  }

  useEffect(() => {
    // Handler for browser close/refresh
    const handleBeforeUnload = async (e) => {
      // e.preventDefault();
      // e.returnValue = ''; // Required for Chrome

      try {
        await updateStudyStatus('');
      } catch (error) {
        console.error('Failed to reset status on exit:', error);
      }
    };

    const updateStudyStatus = async (status) => {
      makePostCall("/close-report", { order_id: pacs_order.pacs_ord_id, status: status })
    }

    // Set up keep-alive ping
    const pingInterval = setInterval(async () => {
      try {
        await makePostCall("/study-ping", { order_id: pacs_order.pacs_ord_id, status: 'R' });
      } catch (error) {
        console.error('Keep-alive ping failed:', error);
      }
    }, 15000); // Every 15 seconds

    // Add event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearInterval(pingInterval);
    };
  }, [patientDetails]);

  useEffect(() => {
    if (patientDetails) {
      setCorrelated(pacs_order?.po_correlated);
      setDiagnosed(pacs_order?.po_diagnosed);
    }
  }, [patientDetails]);

  const refreshAfterUpdate = (status) => {
    fetchPrevReports();
    // disabling automatic actions after saving report.
    // handlePostSaving && handlePostSaving(status, patientDetails);
  }

  // Extract header, body, and footer from editor content
  const extractReportParts = (editorContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editorContent, 'text/html');

    const headerElement = doc.querySelector('.page-header-content');
    const footerElement = doc.querySelector('.page-footer-content');

    // Extract and remove header/footer from doc
    const header = headerElement ? headerElement.outerHTML : '';
    const footer = footerElement ? footerElement.outerHTML : '';
    if (headerElement) headerElement.remove();
    if (footerElement) footerElement.remove();

    // Remaining content is body
    const body = doc.body.innerHTML;

    return { header, body, footer };
  };

  // Update header timestamp when signing off report
  const updateHeaderWithSignoffTime = (editorContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editorContent, 'text/html');

    // Find header element
    const headerElement = doc.querySelector('.page-header-content');
    if (!headerElement) {
      return editorContent; // No header to update
    }

    // Find the Report Date/Time cell
    const cells = headerElement.querySelectorAll('td, th, div, span');
    for (let i = 0; i < cells.length; i++) {
      const text = cells[i].textContent.trim();

      // Check if this cell contains "Report Date" or "Report Time" label
      if (text.match(/Report\s+Date|Report\s+Time/i)) {
        // Find the value cell - it could be:
        // 1. Next sibling's next sibling (skip the colon separator)
        // 2. Just next sibling for table structures
        // 3. Parent's next sibling for different layouts
        let valueCell = cells[i].nextElementSibling?.nextElementSibling;

        if (!valueCell || valueCell.textContent.trim() === ':') {
          // If we got the colon, try just the next sibling
          valueCell = cells[i].nextElementSibling;
        }

        if (!valueCell || valueCell.textContent.trim() === ':') {
          // Try parent's next sibling's child for table structures
          valueCell = cells[i].parentElement?.nextElementSibling?.querySelector('td');
        }

        if (valueCell && valueCell.textContent.trim() !== ':') {
          // Update with current timestamp
          const now = new Date();
          const formattedTime = `${now.getDate().toString().padStart(2, '0')}/${(now.getMonth() + 1).toString().padStart(2, '0')}/${now.getFullYear()} ${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;
          // Use innerHTML with strong tag to match the format
          valueCell.innerHTML = `<strong>${formattedTime}</strong>`;
          break;
        }
      }
    }

    return doc.body.innerHTML;
  };

  const handleSave = (newContent, status, curReport, moreInfo = {}) => {
    // Convert frontend status code to backend format
    const backendStatus = statusMap[status] || status;

    let contentToSave = newContent;

    // Update header timestamp if signing off
    if (status === 'SIGNEDOFF') {
      contentToSave = updateHeaderWithSignoffTime(contentToSave);
    }

    // Extract 3 parts from content
    const { header, body, footer } = extractReportParts(contentToSave);

    if (onSave) {
      onSave({ header, body, footer }, backendStatus, curReport, { ...moreInfo, proxy_user: proxyUser, co_signing_doctor: cosigningDoctor, correlated, diagnosed }, refreshAfterUpdate)
    } else {
      saveReport({ header, body, footer }, backendStatus, curReport, { ...moreInfo, proxy_user: proxyUser, co_signing_doctor: cosigningDoctor }, refreshAfterUpdate)
    };
  }

  const saveReport = (reportParts, status, currentReport, moreInfo, callback) => {
    const { patient } = pacs_order;
    const { proxy_user, co_signing_doctor } = moreInfo;

    makePostCall('/submit-report', {
      html: reportParts.body,
      header: reportParts.header,
      footer: reportParts.footer,
      yh_no: patient?.pat_pin,
      order_no: pacs_order?.po_his_ord_no,
      acc_no: pacs_order?.po_acc_no,
      order_id: pacs_order.pacs_ord_id,
      user_id: getUserDetails()?.username,
      ...moreInfo,
      proxy_user: proxy_user,
      co_signing_doctor: co_signing_doctor,
      status, // This should now be the backend format
      report_id: currentReport?.pr_id,
      // Add signed-off editing metadata
      is_editing_signedoff: isSignedOff,
      edited_at: isSignedOff ? new Date().toISOString() : undefined,
      edited_by: isSignedOff ? getUserDetails()?.username : undefined,
      // correlated: correlated,
      // diagnosed: diagnosed,
    })
      .then(res => {
        callback && callback(status);
      })
      .catch(e => {
        console.log(e);
      });
  }

  const fetchRadUsers = () => {
    makeGetCall('/get-rad-users')
      .then(res => {
        setRadUsers(res.data?.data || []);
      })
      .catch(e => {
        console.log(e);
        setRadUsers([]);
      })
  }

  const fetchAllUsers = () => {
    makePostCall('/user-list', {})
      .then(res => {
        setAllUsers(res.data?.data || []);
      })
      .catch(e => {
        console.log(e);
        setAllUsers([]);
      })
  }

  const fetchPrevReports = () => {
    makePostCall('/get-reports', {
      order_id: pacs_order.pacs_ord_id
    })
      .then(res => {
        const resp_data = res.data?.data || [];
        setReportsData(resp_data);

        // Check if there's a signed-off report
        const hasSignedOffReport = resp_data.some(r => r.pr_status === 'SIGNEDOFF');
        setIsSignedOff(hasSignedOffReport);

        if (resp_data.length > 0) {
          const updated = resp_data.find(r => r.pr_id === currentReport?.pr_id) || resp_data[0];
          setCurrentReport(updated);
        } else {
          setCurrentReport({pr_report_html: "<div></div>"});
        }
      })
      .catch(e => {
        console.log(e);
      })
  }

  const getTemplates = () => {
    makeGetCall(`/get-templates?modality=${pacs_order?.po_modality}&node=${selectedNode}`)
      .then(res => {
        setTemplates(res?.data?.data || []);
      })
      .catch(e => {
        console.log(e);
      })
  }

  const getNodes = () => {
    makeGetCall(`/get-nodes?modality=${pacs_order?.po_modality}`)
      .then(res => {
        setNodes(res?.data?.data || []);
      })
      .catch(e => {
        console.log(e);
      })
  }

  useEffect(() => {
    if (selectedNode) {
      getTemplates()
    }
  }, [selectedNode]);

  // Auto-select node based on body part
  useEffect(() => {
    if (nodes.length > 0 && pacs_order?.po_body_part && !selectedNode) {
      const matchingNode = nodes.find(node => node.code === pacs_order.po_body_part);
      if (matchingNode) {
        setSelectedNode(matchingNode.code);
      }
    }
  }, [nodes, pacs_order?.po_body_part, selectedNode]);

  // Auto-select first template when no existing reports
  useEffect(() => {
    if (templates.length > 0 &&
        currentReport?.pr_report_html === "<div></div>" &&
        !selectedTemplate &&
        editorInitialized) {
      const firstTemplate = templates[0];
      setSelectedTemplate(firstTemplate.template_html);
      handleTemplateChange(firstTemplate.template_html);
    }
  }, [templates, currentReport?.pr_report_html, selectedTemplate, editorInitialized]);

  useEffect(() => {
    if (currentReport) {
      // Check if report has stored header and footer
      if (currentReport.pr_report_header && currentReport.pr_report_footer) {
        // Existing report: Reconstruct full content with stored parts
        const fullContent = `${currentReport.pr_report_header}${currentReport.pr_report_html}${currentReport.pr_report_footer}`;
        setContent(fullContent);
      } else {
        // New/legacy report: Just use body, TinyEditor will add fresh header/footer
        setContent(currentReport.pr_report_html || '');
      }
    }
  }, [currentReport]);


  const loadTemplate = (rec) => {
    setCurrentReport(rec);
  };

  const viewPriorReport = (rec) => {
    /*
    // Commented out modal viewer on 2025-07-04
    // as per new feature to reuse prior report as template

    makePostCall('/print-acc-report', {
      order_id: rec.pr_pacs_ord_id,
    }, {
      responseType: "arraybuffer",
    })
      .then(res => {
        setViewerModal({ visible: true, data: res.data });
      }).catch(e => {
        console.log(e);
        setViewerModal({ visible: false, data: null });
      })
    */

    showLoadConfirmation(rec?.pr_report_html || "<div></div>");
  }

  const handleDelete = (rec) => {
    makePostCall('/delete-report', {
      report_id: rec?.pr_id,
      yh_no: patient?.pat_pin,
      order_id: rec?.pr_pacs_ord_id,
    })
      .then(res => {
        fetchPrevReports();
      })
      .catch(e => {
        console.log(e);
      });
  }

  const handleTemplateChange = (val) => {
    showLoadConfirmation(val);
    // fetch(`/templates/${val}.html`)
    //   .then(response => response.text())
    //   .then(html => {
    //     // Do something with the HTML content
    //     // setContent(html)
    //     setCurrentReport({ pr_html: html });
    //   })
    //   .catch(error => console.error('Error:', error));
  }

  const radUserOptions = useMemo(() => {
    return radUsers.map(user => ({
      label: user.user_fullname,
      value: user.username,
      data: user
    }));

  }, [radUsers]);

  const allUserOptions = useMemo(() => {
    return allUsers.map(user => ({
      label: user.user_fullname,
      value: user.username
    }));
  }, [allUsers]);

  const handleSaveForm = (statusCode) => {
    setSubmitTrigged(true);
    // if (!correlated || !diagnosed) {
    //   message.error("Please select the Correlated & Diagnosed options");
    //   return;
    // }

    // Get content directly from the editor ref - this includes header and footer
    const currentContent = editorRef.current?.getContent() || content;

    // Remove cosign element but KEEP header and footer (they'll be extracted in handleSave)
    const cleanedHtml = removeContentById(currentContent, 'cosign');

    // Pass the full content (with header/footer) - extractReportParts will separate them
    // Pass the database status code directly (DRAFTED, SIGNEDOFF)
    handleSave(cleanedHtml, statusCode, currentReport);
  }

  const handlePrint = () => {
    // Get content directly from the editor ref
    const currentContent = editorRef.current?.getContent() || content;
    const medicalContent = extractMedicalContent(currentContent);

    makePostCall('/print-report', {
      report: currentReport,
      patDetails: patientDetails,
      html: medicalContent, //.replaceAll(' ', '&nbsp'),
      order_id: pacs_order.pacs_ord_id
    }, {
      responseType: "arraybuffer",
    })
      .then(res => {
        const pdfBlob = new Blob([res.data], { type: "application/pdf" });
        const pdf_url = URL.createObjectURL(pdfBlob);
        // setPdfUrl(url);
        const printWindow = window.open(pdf_url, "_blank");
        printWindow.print();
      })
      .catch(err => {
        console.log("Error", err);
      })
  }

  const handleNotification = () => {
    setCriticalFindingModal({ visible: true, data: patientDetails })
  }

  const goToRadiologyDesk = (patDetails) => {
    const { pacs_order } = patDetails
    const { patient, po_site } = pacs_order;
    const { pat_pin } = patient;
    window.open(`${RADIOLOGY_URL(pat_pin, po_site || '')}`, '_blank')
  }

  const handleCosigning = (val, opt) => {
    const { data } = opt;
    const { user_signature } = data;
    setCosigningDoctor(val);
    setCurrentReport({ ...currentReport, pr_report_html: `${currentReport?.pr_report_html} \n\n <div id="cosign">${user_signature}</div>` })
  }

  const showLoadConfirmation = (newHtml) => {
    // Helper function to check if content is empty
    const isContentEmpty = (contentToCheck) => {
      if (!contentToCheck) return true;

      const trimmedContent = contentToCheck.trim();
      if (trimmedContent === "") return true;
      if (trimmedContent === "<div></div>") return true;
      if (trimmedContent === "<div><br></div>") return true;
      if (trimmedContent === "<p></p>") return true;
      if (trimmedContent === "<p><br></p>") return true;

      // Remove all HTML tags and check if there's any actual text content
      const textContent = trimmedContent.replace(/<\/?[^>]+(>|$)/g, "").trim();
      if (textContent === "" || textContent === "&nbsp;" || textContent.replace(/&nbsp;/g, "").trim() === "") {
        return true;
      }

      return false;
    };

    // Check both the current content state AND the currentReport content
    const isCurrentContentEmpty = isContentEmpty(content);
    const isCurrentReportEmpty = isContentEmpty(currentReport?.pr_report_html);

    const isEditorEmpty = isCurrentContentEmpty;

    if (isEditorEmpty) {
      // If editor is empty, directly load the template without showing popup
      setCurrentReport((prev) => ({
        ...prev,
        pr_id: undefined,
        pr_report_html: newHtml,
        pr_status: 'DRAFTED',
      }));

      // Also update the content state to keep them in sync
      setContent(newHtml);
    } else {
      // If editor has content, show the confirmation popup
      setPendingHtml(newHtml);
      setLoadModalVisible(true);
    }
  };

  // Helper functions for button logic
  const getButtonDisabledState = (statusCode, currentStatus) => {
    // Create status order mapping from database
    const statusOrderMap = {};
    statusList.forEach(status => {
      statusOrderMap[status.status_code] = status.status_order;
    });

    const currentOrder = statusOrderMap[currentStatus] || 0;
    const targetOrder = statusOrderMap[statusCode] || 0;

    switch (statusCode) {
      case 'DRAFTED':
        // Disable DRAFT button if editing a signed-off report OR if current status is beyond draft
        return isSignedOff || currentOrder > targetOrder;
      case 'SIGNEDOFF':
        return !['hod', 'radiologist'].includes(userType);
      default:
        return false;
    }
  };

  const getButtonProps = (statusCode) => {
    switch (statusCode) {
      case 'SIGNEDOFF':
        return { type: 'primary', className: 'footer-btn-signoff' };
      case 'DRAFTED':
        return { type: 'default', className: 'footer-btn' };
      default:
        return { type: 'default', className: 'footer-btn' };
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Status style function (same as in pacs/constants.tsx)
  const getStatusStyle = (status) => {
    const baseStyle = {
      height: '1.25rem',
      borderRadius: '0.25rem',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 500,
      fontSize: '0.75rem',
      lineHeight: '1.25rem',
      letterSpacing: '0.06px',
      verticalAlign: 'middle',
      border: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center'
    };

    switch (status) {
      case 'SCANNED':
        return {
          ...baseStyle,
          width: '4.375rem',
          padding: '0 0.375rem 0 0.375rem',
          background: '#DEF0FF',
          color: '#003FD2'
        };
      case 'SIGNEDOFF':
        return {
          ...baseStyle,
          width: '5rem',
          padding: '0 0.375rem 0 0.375rem',
          background: '#DAF3EE',
          color: '#00795C'
        };
      case 'DRAFTED':
        return {
          ...baseStyle,
          width: '4.0625rem',
          padding: '0 0.375rem 0 0.375rem',
          background: '#FEECD5',
          color: '#AC5000'
        };
      default:
        return {
          ...baseStyle,
          width: 'auto',
          padding: '0 0.375rem 0 0.375rem',
          background: '#f0f0f0',
          color: '#666666'
        };
    }
  };

  // Render report list item
  const renderReportItem = (record) => {
    const menu = (
      <Menu>
        <Menu.Item
          key="delete"
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record)}
          disabled={isSignedOff}
        >
          Delete
        </Menu.Item>
        <Menu.Item
          key="load"
          icon={<RightSquareOutlined />}
          onClick={() => loadTemplate(record)}
          disabled={isSignedOff}
        >
          Load
        </Menu.Item>
      </Menu>
    );

    return (
      <div key={record.pr_id} className="report-list-item">
        <div className="report-item-line-1">
          <span>Created by {record.pr_reported_by}</span>
          <Dropdown overlay={menu} trigger={['click']} disabled={isSignedOff}>
            <MoreOutlined
              style={{
                fontSize: '1rem',
                cursor: isSignedOff ? 'not-allowed' : 'pointer',
                color: isSignedOff ? '#d9d9d9' : 'inherit'
              }}
            />
          </Dropdown>
        </div>
        <div className="report-item-line-2">
          {moment(record.createdAt).format("DD-MM-YYYY hh:mm A")}
        </div>
        <div className="report-item-line-3">
          <Tag style={getStatusStyle(record.pr_status)}>
            {record.pr_status?.replaceAll('_', ' ')}
          </Tag>
        </div>
      </div>
    );
  };

  // Render prior report list item
  const renderPriorReportItem = (record) => {
    const menu = (
      <Menu>
        <Menu.Item
          key="view"
          icon={<RightSquareOutlined />}
          onClick={() => viewPriorReport(record)}
          disabled={isSignedOff}
        >
          Load as Template
        </Menu.Item>
      </Menu>
    );

    return (
      <div key={record.pr_id} className="report-list-item">
        <div className="report-item-line-1">
          <span>Created by {record.pr_reported_by}</span>
          <Dropdown overlay={menu} trigger={['click']} disabled={isSignedOff}>
            <MoreOutlined
              style={{
                fontSize: '1rem',
                cursor: isSignedOff ? 'not-allowed' : 'pointer',
                color: isSignedOff ? '#d9d9d9' : 'inherit'
              }}
            />
          </Dropdown>
        </div>
        <div className="report-item-line-2">
          {moment(record.createdAt).format("DD-MM-YYYY hh:mm A")}
        </div>
        <div className="report-item-line-3">
          <Tag style={getStatusStyle(record.pr_status)}>
            {record.pr_status?.replaceAll('_', ' ')}
          </Tag>
        </div>
      </div>
    );
  };

  // Dynamic button rendering using database-driven statuses
  const renderStatusButtons = useMemo(() => {
    if (statusLoading) {
      return <div>Loading...</div>;
    }

    if (statusError || !statusList.length) {
      console.error('Status loading error:', statusError);
      // Fallback to hardcoded buttons with corrected status codes
      return (
        <>
          <Button
            disabled={getButtonDisabledState('DRAFTED', currentReport?.pr_status)}
            className='footer-btn' type='default'
            onClick={() => handleSaveForm('DRAFTED')}
          >DRAFT</Button>
          <Button
            className='footer-btn-signoff'
            onClick={() => handleSaveForm('SIGNEDOFF')}
            disabled={getButtonDisabledState('SIGNEDOFF', currentReport?.pr_status)}
          >
            <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.199951 2.2001C0.199951 1.3176 0.917451 0.600098 1.79995 0.600098H8.19995C9.08245 0.600098 9.79995 1.3176 9.79995 2.2001V12.6276C9.79995 13.2676 9.08745 13.6476 8.55495 13.2926L4.99995 10.9201L1.44495 13.2926C0.912451 13.6476 0.199951 13.2651 0.199951 12.6276V2.2001ZM1.79995 1.8001C1.57995 1.8001 1.39995 1.9801 1.39995 2.2001V11.8801L4.33495 9.9251C4.73745 9.6576 5.26245 9.6576 5.66495 9.9251L8.59995 11.8801V2.2001C8.59995 1.9801 8.41995 1.8001 8.19995 1.8001H1.79995Z" fill="white"/>
            </svg>
            SIGN OFF
          </Button>
        </>
      );
    }

    return statusList
      .filter((status) => {
        // Hide DRAFT button for signed-off reports
        if (isSignedOff && status.status_code === 'DRAFTED') {
          return false;
        }
        return true;
      })
      .map((status) => {
        const isDisabled = getButtonDisabledState(status.status_code, currentReport?.pr_status);
        const buttonProps = getButtonProps(status.status_code);

        return (
          <Button
            key={status.status_id}
            className={buttonProps.className}
            type={buttonProps.type}
            disabled={isDisabled}
            onClick={() => handleSaveForm(status.status_code)} // Use database status_code directly
          >
            {status.status_code === 'SIGNEDOFF' && (
              <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M0.199951 2.2001C0.199951 1.3176 0.917451 0.600098 1.79995 0.600098H8.19995C9.08245 0.600098 9.79995 1.3176 9.79995 2.2001V12.6276C9.79995 13.2676 9.08745 13.6476 8.55495 13.2926L4.99995 10.9201L1.44495 13.2926C0.912451 13.6476 0.199951 13.2651 0.199951 12.6276V2.2001ZM1.79995 1.8001C1.57995 1.8001 1.39995 1.9801 1.39995 2.2001V11.8801L4.33495 9.9251C4.73745 9.6576 5.26245 9.6576 5.66495 9.9251L8.59995 11.8801V2.2001C8.59995 1.9801 8.41995 1.8001 8.19995 1.8001H1.79995Z" fill="white"/>
              </svg>
            )}
            {status.status_label}
          </Button>
        );
      });
  }, [statusList, statusLoading, statusError, currentReport?.pr_status, userType, isSignedOff]);

  return (
    <div className="editor-container">
      <div className="editor-section">
        <div className="section-header">
          <div className="section-header-title">Medical Report</div>
        </div>
        {/* Signed-off Report Indicator Banner */}
        {isSignedOff && (
          <div style={{
            backgroundColor: '#FFF7E6',
            border: '1px solid #FFD591',
            borderRadius: '4px',
            padding: '12px 16px',
            margin: '12px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <ExclamationCircleOutlined style={{ color: '#FA8C16', fontSize: '16px' }} />
            <span style={{
              color: '#873800',
              fontSize: '14px',
              fontWeight: 500
            }}>
              Editing Signed-off Report: Template and history loading options are disabled.
            </span>
          </div>
        )}
        <div className="section-content">
          <RichTextEditor
            ref={editorRef}
            fromReporting={true}
            patDetails={patientDetails}
            currentReport={currentReport}
            cancel={cancel}
            content={content || "<div></div>"}
            onSave={handleSave}
            onChange={handleContentChange}
          />
        </div>
        <div className="section-footer">
          {/* Use Template Button with Popover - Disabled for signed-off reports */}
          <Popover
            content={
              isSignedOff ? (
                <div style={{ padding: '8px' }}>
                  Templates cannot be used when editing a signed-off report
                </div>
              ) : (
                <div style={{ width: 300 }}>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ marginBottom: 8, fontWeight: 500 }}>Node</div>
                    <Select
                      allowClear
                      style={{ width: '100%' }}
                      placeholder="Select Node"
                      value={selectedNode}
                      onChange={(val) => {
                        setSelectedNode(val);
                        setTemplates([]);
                        setSelectedTemplate(null);
                      }}
                      onClear={() => {
                        setSelectedNode(null);
                        setTemplates([]);
                        setSelectedTemplate(null);
                      }}
                      options={nodes?.map((itm) => ({
                        label: itm?.label,
                        value: itm?.code
                      }))}
                    />
                  </div>
                  <div>
                    <div style={{ marginBottom: 8, fontWeight: 500 }}>Template</div>
                    <Select
                      allowClear
                      style={{ width: '100%' }}
                      placeholder="Select Template"
                      value={selectedTemplate}
                      onChange={(val) => {
                        if (val) {
                          setSelectedTemplate(val);
                          handleTemplateChange(val);
                        } else {
                          setSelectedTemplate(null);
                        }
                      }}
                      onClear={() => {
                        setSelectedTemplate(null);
                      }}
                      options={templates?.map((itm) => ({
                        label: itm.rt_display_name,
                        value: itm.template_html,
                        key: itm.rt_template_name
                      }))}
                      disabled={!selectedNode}
                    />
                  </div>
                </div>
              )
            }
            title={isSignedOff ? "Templates Disabled" : "Select Template"}
            trigger="click"
            placement="topLeft"
          >
            <Button
              className='use-template-btn'
              type='default'
              disabled={isSignedOff}
            >
              <FileTextOutlined />
              Use Template
            </Button>
          </Popover>

          {/* Voice Transcribe Button */}
          <VoiceTranscribeButton
            onTranscriptionText={(text, isPartial, replacePartial) => {
              // Call the handleTranscriptionText method from TinyEditor via ref
              if (editorRef.current?.handleTranscriptionText) {
                editorRef.current.handleTranscriptionText(text, isPartial, replacePartial);
              }
            }}
            size="small"
            className="voice-transcribe-btn"
          />

          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.75rem' }}>
            <Button className='footer-btn' type='default' onClick={cancel}>Cancel</Button>
            <Button className='footer-btn' type='default' onClick={handlePrint}>PRINT REPORT</Button>
            {/* Database-driven status buttons */}
            {renderStatusButtons}
          </div>
        </div>
      </div>

      <div className="control-section">
        <div className="section-header">
          <div className="section-header-title">Study Details</div>
        </div>
        <div className="section-content">
          <Card className="mb-3 patient-details-card">
          <div className="!patient-details">
            <div className="patient-info-line-1">
              <UserOutlined className="profile-icon" />
              <div className="pat-name">
                {`${patient?.pat_name}, ${patient?.pat_pin || ''}`}
              </div>
            </div>
            <div className="patient-info-line-2">
              {`${patient?.pat_sex} / ${patient?.pat_dob && isValidDate(patient.pat_dob) ? moment(patient?.pat_dob).fromNow(true) : '-'}`}
            </div>
            <div className="patient-info-line-3">
              {`${pacs_order?.po_modality} / ${pacs_order?.po_ref_doc || "-"}`}
            </div>
            <Button
              className="launch-viewer-btn"
              type="default"
              onClick={
                async () => {
                  const viewerWindow = await launchViewerOnDualMonitor(patientDetails?.ps_study_uid, patientDetails);
                  if (!viewerWindow) message.warning('Could not open viewer window');
                }
              }
            >
              Launch Viewer
            </Button>
            {/* <Button danger className="ms-auto" type="default" onClick={() => { goToRadiologyDesk(patientDetails) }}> Radiology Desk</Button> */}
          </div>
        </Card>
        {/* Reports History Section */}
        <div className="collapsible-section">
          <div className="collapsible-header" onClick={() => toggleSection('reportsHistory')}>
            <div className="collapsible-header-left">
              <BarChartOutlined className="collapsible-icon" />
              <span className="collapsible-title">Reports History</span>
            </div>
            {expandedSections.reportsHistory ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
          </div>
          {expandedSections.reportsHistory && (
            <div className="collapsible-content">
              {(reportsData || []).length > 0 ? (
                (reportsData || []).map(renderReportItem)
              ) : (
                <div style={{
                  textAlign: 'center',
                  color: '#45556C',
                  padding: '1rem 0',
                  fontFamily: 'Inter Variable, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                  fontSize: '0.875rem'
                }}>
                  No reports available
                </div>
              )}
            </div>
          )}
        </div>

        {/* Prior Reports Section - Conditionally Rendered */}
        {priorReports?.length > 0 && (
          <div className="collapsible-section">
            <div className="collapsible-header" onClick={() => toggleSection('priorReports')}>
              <div className="collapsible-header-left">
                <BarChartOutlined className="collapsible-icon" />
                <span className="collapsible-title">Prior Reports</span>
              </div>
              {expandedSections.priorReports ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
            </div>
            {expandedSections.priorReports && (
              <div className="collapsible-content">
                {(priorReports || []).length > 0 ? (
                  (priorReports || []).map(renderPriorReportItem)
                ) : (
                  <div style={{
                    textAlign: 'center',
                    color: '#45556C',
                    padding: '1rem 0',
                    fontFamily: 'Inter Variable, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                    fontSize: '0.875rem'
                  }}>
                    No prior reports available
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Documents Section */}
        <div className="collapsible-section">
          <div className="collapsible-header" onClick={() => toggleSection('documents')}>
            <div className="collapsible-header-left">
              <FolderOutlined className="collapsible-icon" />
              <span className="collapsible-title">Documents</span>
            </div>
            {expandedSections.documents ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
          </div>
          {expandedSections.documents && (
            <div className="collapsible-content">
              {/* Empty for now - template selection moved to footer */}
            </div>
          )}
        </div>

        {/* Advanced Options Section */}
        {
          showAdvanceOptions && (
            <div className="collapsible-section">
              <div className="collapsible-header" onClick={() => toggleSection('advancedOptions')}>
                <div className="collapsible-header-left">
                  <SettingOutlined className="collapsible-icon" />
                  <span className="collapsible-title">Advanced Options</span>
                </div>
                {expandedSections.advancedOptions ? <UpOutlined className="collapsible-arrow" /> : <DownOutlined className="collapsible-arrow" />}
              </div>
              {expandedSections.advancedOptions && (
                <div className="collapsible-content">
                  <div className="advanced-option-item">
                    <a className="notify-physician-link" onClick={handleNotification}>
                      <BellOutlined className="notify-bell-icon" />
                      Notify Physician
                    </a>
                  </div>

                  <div className="advanced-option-item">
                    <Checkbox
                      onChange={(e) => {
                        setMoreAction(e.target.checked ? 'co_signing' : null)
                      }}
                      className="advanced-option-checkbox"
                    >
                      Report Co-Signing
                    </Checkbox>
                    {moreAction === 'co_signing' && (
                      <Select
                        style={{ width: '100%', marginTop: '0.5rem' }}
                        onChange={(val, opt) => { handleCosigning(val, opt) }}
                        options={radUserOptions}
                        placeholder="Select doctor for co-signing"
                      />
                    )}
                  </div>

                  {SHOW_ALL_ADVANCED_OPTIONS && (
                    <>
                      <div className="advanced-option-item">
                        <Checkbox
                          onChange={(e) => {
                            setMoreAction(e.target.checked ? 'proxy-draft' : null)
                          }}
                          className="advanced-option-checkbox"
                        >
                          Proxy Draft
                        </Checkbox>
                        {moreAction === 'proxy-draft' && (
                          <Select
                            style={{ width: '100%', marginTop: '0.5rem' }}
                            onChange={(val) => { setProxyUser(val) }}
                            options={allUserOptions}
                            placeholder="Select user for proxy draft"
                          />
                        )}
                      </div>

                      <div className="advanced-option-item">
                        <Checkbox
                          onChange={(e) => {
                            setMoreAction(e.target.checked ? 'proxy-signoff' : null)
                          }}
                          className="advanced-option-checkbox"
                        >
                          Proxy Signoff
                        </Checkbox>
                        {moreAction === 'proxy-signoff' && (
                          <Select
                            style={{ width: '100%', marginTop: '0.5rem' }}
                            onChange={(val) => { setProxyUser(val) }}
                            options={radUserOptions}
                            placeholder="Select doctor for proxy signoff"
                          />
                        )}
                      </div>

                      <div className="advanced-option-item advanced-option-radio-group">
                        <div className="radio-label">Clinically diagnosed</div>
                        <Radio.Group
                          value={diagnosed}
                          className={submitTriggered ? (!!diagnosed ? '' : 'error') : ''}
                          onChange={(e) => { setDiagnosed(e.target.value) }}
                        >
                          <Radio value={'diagnosed'}>Yes</Radio>
                          <Radio value={'notdiagnosed'}>No</Radio>
                        </Radio.Group>
                        {submitTriggered && correlatedMandatory && !diagnosed && (
                          <div className="error-message">This field is required</div>
                        )}
                      </div>

                      <div className="advanced-option-item advanced-option-radio-group">
                        <div className="radio-label">Clinically correlated</div>
                        <Radio.Group
                          value={correlated}
                          className={submitTriggered ? (!!correlated ? '' : 'error') : ''}
                          onChange={(e) => { setCorrelated(e.target.value) }}
                        >
                          <Radio value={'correlated'}>Yes</Radio>
                          <Radio value={'notcorrelated'}>No</Radio>
                        </Radio.Group>
                        {submitTriggered && correlatedMandatory && !correlated && (
                          <div className="error-message">This field is required</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )
        }
        </div>
      </div>

      {criticalFindingModal && (
        <Modal
          width={800}
          open={criticalFindingModal?.visible}
          onCancel={() => { setCriticalFindingModal({ visible: false, data: null }) }}
          okButtonProps={{ style: { display: 'none' } }}
        >
          <CriticalFinding closeNotificationModal={() => { setCriticalFindingModal({}) }} patDetails={criticalFindingModal?.data} />
        </Modal>
        )
      }
      {/*
        // Uncomment this if you wanna show prior reports in a pdfviewer dialog
        viewerModal && (
          <Modal
            width={1000}
            open={viewerModal?.visible}
            onCancel={() => { setViewerModal({ visible: false }) }}
            okButtonProps={{ style: { display: 'none' } }}
          >
            <PdfViewer pdfArrayBuffer={viewerModal?.data} />
          </Modal>
        )
          */
      }
      <Modal
        open={loadModalVisible}
        onCancel={() => setLoadModalVisible(false)}
        footer={[
          <Button
            key="append"
            type="primary"
            onClick={() => {
              setCurrentReport((prev) => ({
                ...prev,
                pr_id: undefined,
                pr_report_html: (prev?.pr_report_html || "") + pendingHtml,
                pr_status: 'DRAFTED',
              }));
              setLoadModalVisible(false);
            }}
          >
            Append
          </Button>,
          <Button
            key="replace"
            danger
            onClick={() => {
              setCurrentReport((prev) => ({
                ...prev,
                pr_id: undefined,
                pr_report_html: pendingHtml,
                pr_status: 'DRAFTED',
              }));
              setLoadModalVisible(false);
            }}
          >
            Replace
          </Button>,
          <Button
            key="cancel"
            onClick={() => setLoadModalVisible(false)}
          >
            Cancel
          </Button>
        ]}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <ExclamationCircleOutlined
            style={{ color: "#faad14", marginRight: '0.5rem', fontSize: "1.25rem" }}
          />
          <b>Load Template / Prior Report</b>
        </div>
        <div style={{ marginTop: '0.75rem' }}>
          Do you want to replace the current content or append this template?
        </div>
      </Modal>
    </div>
  );
};

const extractMedicalContent = (fullContent) => {

  if (!fullContent || fullContent === '<div></div>' || fullContent === '') {
    return '<div></div>';
  }

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = fullContent;

  // Remove ONLY the elements that TinyEditor auto-generates
  // Use the exact same selectors that TinyEditor uses to remove/add
  const elementsToRemove = tempDiv.querySelectorAll(
    '.page-header-content, .page-footer-content, .mce-pagebreak.auto-break'
  );

  elementsToRemove.forEach(el => {
    el.remove();
  });


  const cleanContent = tempDiv.innerHTML.trim();

  const result = cleanContent || '<div></div>';
  return result;
};

export default ReportEditor;
